// Extracted from web/components/ModelCanvas.tsx; behavior and declaration bodies preserved.
import { memo, useEffect, useRef } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";

import { useReactFlow, useViewport } from "@xyflow/react";
import type { EdgeProps } from "@xyflow/react";

import type { Point } from "../../shared/contracts.js";
import { pathToSvg, pathMidpoint, layoutRelationshipLabel } from "../components/canvasGeometry.js";

import { relationshipDashArray } from "../../shared/appearance.js";
import { createRouteDragSession } from "../components/canvasInteraction.js";
import type { EntityNode, RelationEdge } from "./renderTypes.js";

import { EndpointMark } from "./EndpointMark.js";
export const RelationshipEdge = memo(function RelationshipEdge({
  id,
  data,
  selected,
}: EdgeProps<RelationEdge>) {
  const flow = useReactFlow<EntityNode, RelationEdge>();
  const { zoom: routeZoom } = useViewport();
  const activeGesture = useRef<(() => void) | null>(null);
  useEffect(() => () => activeGesture.current?.(), []);
  useEffect(() => { activeGesture.current?.(); }, [data?.readOnly, data?.editVersion]);
  if (!data) return null;
  const { route, notation, connector } = data,
    r = route.relationship,
    middle = pathMidpoint(route.points),
    caption = data.style.label.source === "description" && r.description?.trim() ? r.description : r.name,
    labelAnchor = layoutRelationshipLabel(route.points, data.style.label, caption, data.preferences.fontFamily, 11, data.boxes),
    line = pathToSvg(route.points);
  const gripRadius = (pixels: number) => pixels / Math.max(.12, routeZoom);
  const drag = (
    event: ReactPointerEvent<SVGElement>,
    kind: "source" | "target" | number | { kind: "segment" | "bend"; index: number },
  ) => {
    if (data.readOnly || event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    activeGesture.current?.();
    const element = event.currentTarget, pointerId = event.pointerId;
    const start = flow.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const baselineRoute = route;
    element.setPointerCapture(pointerId);
    data.onGestureActiveChange?.(true);
    const session = createRouteDragSession(pointerId, start, routeZoom, (point, delta, complete) => {
      if (typeof kind === "object") data.onRouteEdit(id, baselineRoute, kind.kind, kind.index, delta, complete);
      else if (typeof kind === "number") data.onWaypoint(id, kind, point, complete);
      else data.onEndpoint(id, kind, point, complete);
    }, data.onCancel);
    const flowPoint = (e: PointerEvent) => flow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const move = (e: PointerEvent) => { session.move(e.pointerId, flowPoint(e)); };
    const end = (e: PointerEvent) => { if (session.finish(e.pointerId, flowPoint(e))) cleanup(); };
    const cancel = () => { session.cancel(); cleanup(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); cancel(); } };
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("blur", cancel);
      window.removeEventListener("keydown", key, true);
      if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
      activeGesture.current = null;
      data.onGestureActiveChange?.(false);
    };
    activeGesture.current = cancel;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("blur", cancel);
    window.addEventListener("keydown", key, true);
  };
  return (
    <g
      className={`nopan dg-canvas-relationship ${selected ? "is-selected" : ""} ${route.warnings.length ? "has-warning" : ""}`}
      style={{ "--dg-relationship-color": data.style.color, "--dg-relationship-width": data.style.width, fontFamily: data.preferences.fontFamily } as CSSProperties}
    >
      <path
        d={line}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        vectorEffect="non-scaling-stroke"
        className="nopan react-flow__edge-interaction"
      />
      {selected && <path d={line} className="dg-canvas-edge-halo" />}
      <path
        d={line}
        className="dg-canvas-edge-line"
        strokeWidth={data.style.width}
        strokeDasharray={
          relationshipDashArray(data.style, notation, r.identifying)
        }
      />
      {selected && !data.readOnly && route.points.slice(0, -1).map((point, index) => {
        const next = route.points[index + 1];
        return <path key={`segment-${index}`} data-route-segment={index} aria-label={`Drag relationship segment ${index + 1}`} d={`M ${point.x} ${point.y} L ${next.x} ${next.y}`} fill="none" stroke="transparent" strokeWidth={16} vectorEffect="non-scaling-stroke" className="nopan nodrag dg-canvas-route-segment" onPointerDown={event => drag(event, { kind: "segment", index })}><title>Drag this segment in any direction; Escape cancels</title></path>;
      })}
      <EndpointMark
        point={route.sourceAttachment.point}
        side={route.sourceAttachment.side}
        many={r.sourceCardinality === "many"}
        optional={r.sourceOptional}
        notation={notation}
        identifying={r.identifying}
      />
      <EndpointMark
        point={route.targetAttachment.point}
        side={route.targetAttachment.side}
        many={r.targetCardinality === "many"}
        optional={r.targetOptional}
        notation={notation}
        identifying={r.identifying}
      />
      {notation === "chen" && (
        <path
          transform={`translate(${middle.x} ${middle.y})`}
          d="M -44 0 L 0 -18 L 44 0 L 0 18 Z"
          className="dg-canvas-chen-diamond"
        />
      )}
      {labelAnchor.fallback && data.preferences.showRelationshipNames !== false && data.style.label.visible !== false && <path d={`M ${labelAnchor.routePoint.x} ${labelAnchor.routePoint.y} L ${labelAnchor.x} ${labelAnchor.y}`} stroke={data.style.color} strokeDasharray="2 3" fill="none"/>}
      {data.preferences.showRelationshipNames !== false && data.style.label.visible !== false && r.name && (
        <g
          className="dg-canvas-relationship-label"
          data-orientation={data.style.label.orientation}
          data-placement={data.style.label.placement}
          data-anchor={data.style.label.position}
          transform={`translate(${labelAnchor.x} ${labelAnchor.y}) rotate(${labelAnchor.angle})`}
        >
          <title>{caption}</title>
          {(data.style.label.placement === "on-line" || labelAnchor.fallback) && (
            <rect
              x={-labelAnchor.width/2} y={-labelAnchor.height/2}
              width={labelAnchor.width} height={labelAnchor.height} rx={5}
              className="dg-canvas-label-bg"
            />
          )}
          <text textAnchor="middle" dominantBaseline="central" className="dg-canvas-relation-label">
            {labelAnchor.lines.map((line,index)=><tspan key={index} x={0} y={(index-(labelAnchor.lines.length-1)/2)*16}>{line}</tspan>)}
          </text>
        </g>
      )}
      {selected && !data.readOnly && (
        <>
          {route.points.slice(1, -1).map((point, index) => <circle key={`bend-${index + 1}`} data-route-bend={index + 1} aria-label={`Drag relationship bend ${index + 1}`} cx={point.x} cy={point.y} r={gripRadius(4)} className="nopan nodrag dg-canvas-route-bend" onPointerDown={event => drag(event, { kind: "bend", index: index + 1 })}><title>Drag bend {index + 1} in any direction; Escape cancels</title></circle>)}
          {[
            ["source", route.sourceAttachment.point],
            ["target", route.targetAttachment.point],
          ].map(([kind, p]) => {
            const point = p as Point;
            return (
              <circle
                key={kind as string}
                cx={point.x}
                cy={point.y}
                r={gripRadius(6)}
                className="nodrag nopan dg-canvas-endpoint-grip"
                onPointerDown={(e) => drag(e, kind as "source" | "target")}
              >
                <title>
                  Drag {kind as string} attachment to another side of the same
                  entity
                </title>
              </circle>
            );
          })}
          {(connector?.waypoints ?? []).map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={gripRadius(5)}
              className="nodrag nopan dg-canvas-waypoint"
              onPointerDown={(e) => drag(e, i)}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                data.onRouteMenu(id, e.clientX, e.clientY, p, i);
              }}
            >
              <title>Drag waypoint {i + 1}; right-click to remove</title>
            </circle>
          ))}
        </>
      )}
    </g>
  );
});
